import { change, getFormValues } from 'redux-form'
import { SEMINAR } from '@myap/metadata'
import { Input, Tooltip } from '../../common'
import {
  SELECT_SPECIAL_DIGITAL_FORMATS_FORM,
  SDF_EXTENDED_TIME,
  SDF_MULTIDAY,
  SDF_ACCESSIBILITY,
  SDF_ADDITIONAL_ACCOMMODATIONS,
  SDF_PAPER_BASED_EXAM,
  SDF_FREE_RESPONSE_BOOKLET,
  SDF_REFERENCE_SHEET,
  PAPERFORMATS_PAPER,
  PAPERFORMATS_BRAILLE,
  PAPERFORMATS_CUSTOM_SIZE,
  PAPERFORMATS_14POINT,
  PAPERFORMATS_20POINT,
  PAPERFORMATS_LARGEPRINT_A,
  PAPERFORMATS_LARGEPRINT_B,
  PAPERFORMATS_WRITERSCRIBE,
  HUMAN_READER_PAPER,
  HUMAN_READER_DIGITAL,
  MULTIDAY_ONLY2DAY,
  MULTIDAY_OVER2DAY,
  MULTIDAY_MULTIDAY,
  PAPERFORMATS_SORT_ORDER,
  MULTIDAY_SORT_ORDER,
} from '../../../constants/StudentConstants'
import { sortColumnByKey } from '../../../utils/sort'
import { getAccommodationDescription } from '../../../selectors/section'
import { getDeadlineIsPast } from '../../../selectors/settings'

import styles from '../../../assets/style/scss/materials-modal.scss'

const ExtendedTimeDigitalMessaging = () => (
  <p>
    For digital exams, extended time will be applied at the part(s) or section(s) level, depending
    on what type of extended time the student has been approved for. If the student is approved for
    multiple types of extended time, the greatest amount of approved extended time applicable for
    the part or section will be provided.
  </p>
)

const ExtendedTimeHybridMessaging = () => (
  <p>
    For hybrid exams, extended time will be applied at the part(s) or section(s) level, depending on
    what type of extended time the student has been approved for. If the student is approved for
    multiple types of extended time, the greatest amount of approved extended time applicable for
    the part or section will be provided.
  </p>
)

const MultiDayMessaging = ({ hasOnlyOneOption, testCd }) => (
  <>
    <p>
      This student has approved accommodations that may make them eligible for multiple-day testing
      on this exam. Confirm with the SSD coordinator and the student whether multiple-day testing
      will be needed for this exam before submitting any update.{' '}
      <strong>
        If the student does need multiple-day testing for this exam, you need to select it here and
        update your exam order.
      </strong>
    </p>
    {hasOnlyOneOption ? (
      testCd === SEMINAR ? (
        <p>
          <strong>
            Note: Multiple-day testing is not supported digitally for{' '}
            <span style={{ textDecoration: 'underline', whiteSpace: 'nowrap' }}>Seminar</span>.
          </strong>{' '}
          If this student&#39;s accommodations require them to test over multiple days and
          &quot;Multiple-Day Testing&quot; is selected, the student will be provided with a
          paper-based exam.
        </p>
      ) : (
        <p>
          <strong>
            Note: Multiple-day testing for digital exams enables a student to test over{' '}
            <span style={{ textDecoration: 'underline', whiteSpace: 'nowrap' }}>2 days</span>.
          </strong>{' '}
          If this student&#39;s accommodations require them to test over more than 2 days, contact
          the College Board SSD office.
        </p>
      )
    ) : (
      <p>
        <strong>
          Note: Multiple-day testing for digital exams enables a student to test over{' '}
          <span style={{ textDecoration: 'underline', whiteSpace: 'nowrap' }}>2 days</span>
        </strong>
        . If this student&#39;s accommodations require them to test over more than 2 days and
        &quot;More than 2 day Multiple-Day Testing&quot; is selected, the student will be provided
        with a paper-based exam.
      </p>
    )}
  </>
)

const AdditionalAccommodations = () => (
  <p>
    This student has approved accommodations that do not impact the digital test format, such as
    modified test settings and other accommodations. Confirm with the SSD coordinator and the
    student whether these accommodations will be needed on test day.
  </p>
)

const PaperBasedExamAccommodations = () => (
  <p>
    This student has approved accommodations that make them eligible for a paper-format of this
    exam. Confirm with the SSD Coordinator and the student whether they want to take the
    paper-format instead of the digital format of the exam.{' '}
    <strong>
      If the student does need a paper-format exam, you need to select it here and update your exam
      order.
    </strong>
  </p>
)

const LargePrintTestBooklet = () => (
  <p>
    This student has an approved accommodation for a large-print format exam and will receive a
    large-print test booklet that is appropriate for their accommodation. The student will write
    their responses in the printed booklet. Students who opt out of receiving a large-print test
    booklet will receive a standard free-response booklet.
  </p>
)

const LargePrintReferenceSheets = () => (
  <p>
    This student has an approved accommodation for a large-print format exam and will receive
    large-print printed reference information that is appropriate for their accommodation. Students
    who opt out of receiving large-print printed reference information will receive standard size
    printed reference information.
  </p>
)

const TooltipInfo = ({ category, code }) => {
  if (category === SDF_ACCESSIBILITY) {
    // Human Reader
    if (code === '025') {
      const HUMANREADERMSG =
        'If a student approved for a human reader plans to take a digital exam using a screen reader, keep their accommodation for human reader selected so the student receives an accessible format of the digital exam.'
      return (
        <Tooltip
          title={HUMANREADERMSG}
          placement="top"
          container="body"
          label="More information: Human Reader"
          style={{ display: 'inline-block', marginLeft: '8px' }}
        >
          <span
            className="cb-glyph cb-glyph-xs cb-glyph-circular cb-exclamation"
            aria-hidden="true"
            data-alt={HUMANREADERMSG}
            style={{ fontSize: '8px' }}
          />
        </Tooltip>
      )
    }
    // Writer/scribe
    if (code === '034') {
      const WRITERSCRIBEMSG =
        'If a student approved for a writer/scribe plans to take a digital exam using voice recognition software, keep their accommodation for writer/scribe selected so the student receives an accessible format of the digital exam.'
      return (
        <Tooltip
          title={WRITERSCRIBEMSG}
          placement="top"
          container="body"
          label="More information: Writer/scribe"
          style={{ display: 'inline-block', marginLeft: '8px' }}
        >
          <span
            className="cb-glyph cb-glyph-xs cb-glyph-circular cb-exclamation"
            aria-hidden="true"
            data-alt={WRITERSCRIBEMSG}
            style={{ fontSize: '8px' }}
          />
        </Tooltip>
      )
    }
    // Pre-recorded audio
    if (code === '026') {
      const PRERECORDEDAUDIOMSG =
        'If a student approved for pre-recorded audio (MP3 via streaming) plans to take a digital exam using a screen reader, keep their accommodation for pre-recorded audio (MP3 via streaming) selected so the student receives an accessible format of the digital exam.'
      return (
        <Tooltip
          title={PRERECORDEDAUDIOMSG}
          placement="top"
          container="body"
          label="More information: Pre-recorded audio"
          style={{ display: 'inline-block', marginLeft: '8px' }}
        >
          <span
            className="cb-glyph cb-glyph-xs cb-glyph-circular cb-exclamation"
            aria-hidden="true"
            data-alt={PRERECORDEDAUDIOMSG}
            style={{ fontSize: '8px' }}
          />
        </Tooltip>
      )
    }
  }

  return null
}

const mapStateToProps = state => {
  const {
    settingsSpecialDigitalFormats: { extendedAccessibleSsdCodes = [] },
  } = state
  return {
    extendedAccessibleSsdCodes,
    selectedSpecialDigitalFormats: getFormValues(SELECT_SPECIAL_DIGITAL_FORMATS_FORM)(state),
    referenceSheetOptOutDeadlineIsPast: getDeadlineIsPast(state, 'referenceSheetOptOutDeadline'),
  }
}

export default connect(mapStateToProps, { change })(
  ({
    categoryTitle,
    categoryName,
    descriptions,
    accommodations,
    extendedAccessibleSsdCodes,
    selectedSpecialDigitalFormats,
    anyMultiDayEligibleSelected,
    isDigital,
    isHybrid,
    testCd,
    accommodationsInfo,
    referenceSheetOptOutDeadlineIsPast,
    change,
  }) => {
    const inlineBlock = { display: 'inline-block' }
    const groupId = `${categoryName}FormGroup`
    const accommodationCodes = Object.keys(accommodations)

    // Utility function to set a given field to a given value
    function toggleField(fieldName, value) {
      change(SELECT_SPECIAL_DIGITAL_FORMATS_FORM, fieldName, value)
    }

    // Utility function, if a particular code is found, set it to given value
    function toggleIfFound(category, code, value) {
      if (selectedSpecialDigitalFormats?.[category]?.[code] !== undefined) {
        toggleField(`${category}.${code}`, value)
      }
    }

    useEffect(() => {
      if (categoryName === SDF_MULTIDAY && !anyMultiDayEligibleSelected) {
        // If hiding multiday, also set it and all child options to false
        toggleIfFound(SDF_MULTIDAY, MULTIDAY_ONLY2DAY, false)
        toggleIfFound(SDF_MULTIDAY, MULTIDAY_OVER2DAY, false)
        MULTIDAY_SORT_ORDER.slice(2).forEach(multidayPaper =>
          toggleIfFound(SDF_MULTIDAY, multidayPaper, false)
        )
      }
    }, [categoryName, anyMultiDayEligibleSelected])

    if (accommodationCodes.length > 0) {
      const accommodationObjects = accommodationCodes.map(cd => ({
        code: cd,
        value: accommodations[cd],
        description: getAccommodationDescription({
          accommodationCode: cd,
          description: descriptions[categoryName][cd],
          accommodations: accommodationsInfo,
        }),
      }))
      // Specifically do not change the order of Paper-based Exam options
      const sortedAccommodations =
        categoryName === SDF_PAPER_BASED_EXAM
          ? accommodationObjects
              .slice()
              .sort(
                (a, b) =>
                  PAPERFORMATS_SORT_ORDER.indexOf(a.code) - PAPERFORMATS_SORT_ORDER.indexOf(b.code)
              )
          : categoryName === SDF_MULTIDAY
          ? accommodationObjects
              .slice()
              .sort(
                (a, b) => MULTIDAY_SORT_ORDER.indexOf(a.code) - MULTIDAY_SORT_ORDER.indexOf(b.code)
              )
          : sortColumnByKey(accommodationObjects, 'description', 'asc')

      if (
        categoryName !== SDF_MULTIDAY ||
        (categoryName === SDF_MULTIDAY && anyMultiDayEligibleSelected)
      ) {
        return (
          <fieldset name={`${categoryName}-form-group`} className={styles['materials-fieldset']}>
            <legend id={groupId}>{categoryTitle}</legend>
            {categoryName === SDF_EXTENDED_TIME && isDigital ? (
              <ExtendedTimeDigitalMessaging />
            ) : categoryName === SDF_EXTENDED_TIME && isHybrid ? (
              <ExtendedTimeHybridMessaging />
            ) : null}
            {categoryName === SDF_MULTIDAY ? (
              <MultiDayMessaging
                hasOnlyOneOption={sortedAccommodations?.length === 1}
                testCd={testCd}
              />
            ) : null}
            {categoryName === SDF_ADDITIONAL_ACCOMMODATIONS ? <AdditionalAccommodations /> : null}
            {categoryName === SDF_PAPER_BASED_EXAM ? <PaperBasedExamAccommodations /> : null}
            {categoryName === SDF_FREE_RESPONSE_BOOKLET ? <LargePrintTestBooklet /> : null}
            {categoryName === SDF_REFERENCE_SHEET ? <LargePrintReferenceSheets /> : null}
            <div role="group" aria-labelledby={groupId} className={styles['materials-radiogroup']}>
              {sortedAccommodations.map(({ code, value, description }) => {
                const disabled =
                  (categoryName === SDF_EXTENDED_TIME &&
                    extendedAccessibleSsdCodes.includes(code)) ||
                  (categoryName === SDF_EXTENDED_TIME && code === HUMAN_READER_PAPER) ||
                  (categoryName === SDF_MULTIDAY &&
                    (code === PAPERFORMATS_LARGEPRINT_A || code === PAPERFORMATS_LARGEPRINT_B) &&
                    selectedSpecialDigitalFormats[SDF_MULTIDAY][PAPERFORMATS_14POINT]) ||
                  (categoryName === SDF_MULTIDAY &&
                    (code === PAPERFORMATS_LARGEPRINT_A || code === PAPERFORMATS_LARGEPRINT_B) &&
                    selectedSpecialDigitalFormats[SDF_MULTIDAY][PAPERFORMATS_20POINT]) ||
                  (categoryName === SDF_MULTIDAY &&
                    (code === PAPERFORMATS_LARGEPRINT_A || code === PAPERFORMATS_LARGEPRINT_B) &&
                    selectedSpecialDigitalFormats[SDF_MULTIDAY][PAPERFORMATS_CUSTOM_SIZE]) ||
                  (categoryName === SDF_PAPER_BASED_EXAM &&
                    (code === PAPERFORMATS_LARGEPRINT_A || code === PAPERFORMATS_LARGEPRINT_B) &&
                    selectedSpecialDigitalFormats[SDF_PAPER_BASED_EXAM][
                      PAPERFORMATS_CUSTOM_SIZE
                    ]) ||
                  (categoryName === SDF_PAPER_BASED_EXAM &&
                    (code === PAPERFORMATS_LARGEPRINT_A || code === PAPERFORMATS_LARGEPRINT_B) &&
                    selectedSpecialDigitalFormats[SDF_PAPER_BASED_EXAM][PAPERFORMATS_14POINT]) ||
                  (categoryName === SDF_PAPER_BASED_EXAM &&
                    (code === PAPERFORMATS_LARGEPRINT_A || code === PAPERFORMATS_LARGEPRINT_B) &&
                    selectedSpecialDigitalFormats[SDF_PAPER_BASED_EXAM][PAPERFORMATS_20POINT]) ||
                  (categoryName === SDF_PAPER_BASED_EXAM &&
                    (code === HUMAN_READER_PAPER || code === HUMAN_READER_DIGITAL) &&
                    selectedSpecialDigitalFormats[SDF_PAPER_BASED_EXAM][PAPERFORMATS_BRAILLE]) ||
                  (categoryName === SDF_REFERENCE_SHEET && referenceSheetOptOutDeadlineIsPast)

                // Indent all options under paperFormats category
                // slice(1) because the first item in the array is Paper, which should not be
                // indented
                // Indent all options under multiday category and over2Day
                // slice(2) because the first and second items in the array should not be
                // indented
                const inputStyle =
                  categoryName === SDF_PAPER_BASED_EXAM &&
                  PAPERFORMATS_SORT_ORDER.slice(1).includes(code)
                    ? { marginLeft: '16px', ...inlineBlock }
                    : categoryName === SDF_MULTIDAY && MULTIDAY_SORT_ORDER.slice(2).includes(code)
                    ? { marginLeft: '16px', ...inlineBlock }
                    : inlineBlock

                return (
                  <div
                    key={`${categoryName}_${code}`}
                    className={styles['material-radiogroup-wrap-full']}
                  >
                    <Input
                      type="checkbox"
                      name={`${categoryName}.${code}`}
                      style={inputStyle}
                      label={
                        <>
                          {description}
                          <TooltipInfo category={categoryName} code={code} />
                        </>
                      }
                      ariaLabel={description}
                      normalize={val => !!val}
                      disabled={disabled}
                      input={{
                        name: `${categoryName}.${code}`,
                        checked: accommodations[code],
                        value,
                        onChange: e => {
                          const val = e.target.value === 'true'
                          toggleField(`${categoryName}.${code}`, !val)
                          if (
                            categoryName === SDF_ACCESSIBILITY &&
                            extendedAccessibleSsdCodes.includes(code)
                          ) {
                            toggleField(`${SDF_EXTENDED_TIME}.${code}`, !val)
                          }
                          // PAPER FORMATS - Select top level Paper-format exam if any child items
                          // selected
                          if (
                            categoryName === SDF_PAPER_BASED_EXAM &&
                            PAPERFORMATS_SORT_ORDER.slice(1).includes(code) &&
                            !val
                          ) {
                            toggleField(`${SDF_PAPER_BASED_EXAM}.${PAPERFORMATS_PAPER}`, true)
                          }
                          // PAPER FORMATS - If top level Paper-format exam item is unchecked,
                          // deselect any child items
                          if (
                            categoryName === SDF_PAPER_BASED_EXAM &&
                            code === PAPERFORMATS_PAPER &&
                            val
                          ) {
                            PAPERFORMATS_SORT_ORDER.slice(1).forEach(paperFormatCode =>
                              toggleIfFound(SDF_PAPER_BASED_EXAM, paperFormatCode, false)
                            )
                          }
                          // MULTIDAY - Select top level Over 2 Day exam or Only 2 Day (if Seminar)
                          // when any child items selected
                          if (
                            categoryName === SDF_MULTIDAY &&
                            MULTIDAY_SORT_ORDER.slice(2).includes(code) &&
                            !val
                          ) {
                            if (testCd === SEMINAR) {
                              toggleIfFound(SDF_MULTIDAY, MULTIDAY_MULTIDAY, true)
                            } else {
                              toggleIfFound(SDF_MULTIDAY, MULTIDAY_OVER2DAY, true)
                              toggleIfFound(SDF_MULTIDAY, MULTIDAY_ONLY2DAY, false)
                            }
                          }
                          // MULTIDAY - If top level Over 2 Day exam item is unchecked,
                          // deselect any child items
                          if (categoryName === SDF_MULTIDAY && code === MULTIDAY_OVER2DAY && val) {
                            MULTIDAY_SORT_ORDER.slice(2).forEach(multidayPaper =>
                              toggleIfFound(SDF_MULTIDAY, multidayPaper, false)
                            )
                          }
                          // MULTIDAY - If exam is Seminar and top level Only 2 Day exam item is
                          // unchecked, deselect any child items
                          if (
                            categoryName === SDF_MULTIDAY &&
                            testCd === SEMINAR &&
                            code === MULTIDAY_MULTIDAY &&
                            val
                          ) {
                            MULTIDAY_SORT_ORDER.slice(2).forEach(multidayPaper =>
                              toggleIfFound(SDF_MULTIDAY, multidayPaper, false)
                            )
                          }
                          // MULTIDAY - If Over 2 Day exam is checked, also check LBTB and LBAS
                          // if available
                          if (
                            categoryName === SDF_MULTIDAY &&
                            (code === MULTIDAY_OVER2DAY ||
                              (testCd === SEMINAR && code === MULTIDAY_MULTIDAY)) &&
                            !val
                          ) {
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_14POINT, true)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_20POINT, true)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_CUSTOM_SIZE, true)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_LARGEPRINT_A, true)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_LARGEPRINT_B, true)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_WRITERSCRIBE, true)
                          }
                          // PAPER FORMATS - If top level Paper-format exam item is checked,
                          // select Large Print Test Book and LPAS
                          if (
                            categoryName === SDF_PAPER_BASED_EXAM &&
                            code === PAPERFORMATS_PAPER &&
                            !val
                          ) {
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_14POINT, true)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_20POINT, true)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_CUSTOM_SIZE, true)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_LARGEPRINT_A, true)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_LARGEPRINT_B, true)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_WRITERSCRIBE, true)
                          }
                          // PAPER FORMATS - If any of the large font size options are selected,
                          // also select the available large block answer sheet item
                          if (
                            categoryName === SDF_PAPER_BASED_EXAM &&
                            (code === PAPERFORMATS_14POINT ||
                              code === PAPERFORMATS_20POINT ||
                              code === PAPERFORMATS_CUSTOM_SIZE) &&
                            !val
                          ) {
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_LARGEPRINT_A, true)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_LARGEPRINT_B, true)
                          }
                          // MULTIDAY - If any of the large font size options are selected,
                          // also select the available large block answer sheet item
                          if (
                            categoryName === SDF_MULTIDAY &&
                            (code === PAPERFORMATS_14POINT ||
                              code === PAPERFORMATS_20POINT ||
                              code === PAPERFORMATS_CUSTOM_SIZE) &&
                            !val
                          ) {
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_LARGEPRINT_A, true)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_LARGEPRINT_B, true)
                          }
                          // Only one of Braille, LPTB-14, LPTB-20, or LPTB-Other may
                          // be selected at a time
                          if (
                            categoryName === SDF_PAPER_BASED_EXAM &&
                            code === PAPERFORMATS_BRAILLE &&
                            !val
                          ) {
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_14POINT, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_20POINT, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_CUSTOM_SIZE, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_14POINT, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_20POINT, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_CUSTOM_SIZE, false)
                          }
                          if (
                            categoryName === SDF_PAPER_BASED_EXAM &&
                            code === PAPERFORMATS_14POINT &&
                            !val
                          ) {
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_BRAILLE, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_20POINT, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_CUSTOM_SIZE, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_20POINT, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_CUSTOM_SIZE, false)
                          }
                          if (
                            categoryName === SDF_PAPER_BASED_EXAM &&
                            code === PAPERFORMATS_20POINT &&
                            !val
                          ) {
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_BRAILLE, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_14POINT, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_CUSTOM_SIZE, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_14POINT, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_CUSTOM_SIZE, false)
                          }
                          if (
                            categoryName === SDF_PAPER_BASED_EXAM &&
                            code === PAPERFORMATS_CUSTOM_SIZE &&
                            !val
                          ) {
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_BRAILLE, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_14POINT, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_20POINT, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_14POINT, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_20POINT, false)
                          }
                          // MULTIDAY - Only one of LPTB-14, LPTB-20, or LPTB-Other may be selected
                          // at a time
                          if (
                            categoryName === SDF_MULTIDAY &&
                            code === PAPERFORMATS_14POINT &&
                            !val
                          ) {
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_BRAILLE, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_20POINT, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_CUSTOM_SIZE, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_20POINT, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_CUSTOM_SIZE, false)
                          }
                          if (
                            categoryName === SDF_MULTIDAY &&
                            code === PAPERFORMATS_20POINT &&
                            !val
                          ) {
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_BRAILLE, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_14POINT, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_CUSTOM_SIZE, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_14POINT, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_CUSTOM_SIZE, false)
                          }
                          if (
                            categoryName === SDF_MULTIDAY &&
                            code === PAPERFORMATS_CUSTOM_SIZE &&
                            !val
                          ) {
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_BRAILLE, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_14POINT, false)
                            toggleIfFound(SDF_MULTIDAY, PAPERFORMATS_20POINT, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_14POINT, false)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, PAPERFORMATS_20POINT, false)
                          }
                          // Only one of ONLY2DAY or OVER2DAY may be selected at a time
                          if (categoryName === SDF_MULTIDAY && code === MULTIDAY_ONLY2DAY && !val) {
                            MULTIDAY_SORT_ORDER.slice(1).forEach(multidayPaper =>
                              toggleIfFound(SDF_MULTIDAY, multidayPaper, false)
                            )
                          }
                          if (categoryName === SDF_MULTIDAY && code === MULTIDAY_OVER2DAY && !val) {
                            toggleIfFound(SDF_MULTIDAY, MULTIDAY_ONLY2DAY, false)
                          }
                          // Checked status of Human Reader under Extended Time category should
                          // mirror checked status of same under Additional Accommodations category
                          if (
                            categoryName === SDF_ADDITIONAL_ACCOMMODATIONS &&
                            code === HUMAN_READER_PAPER
                          ) {
                            toggleIfFound(SDF_EXTENDED_TIME, HUMAN_READER_PAPER, !val)
                          }
                          if (
                            categoryName === SDF_ADDITIONAL_ACCOMMODATIONS &&
                            code === HUMAN_READER_DIGITAL
                          ) {
                            toggleIfFound(SDF_EXTENDED_TIME, HUMAN_READER_DIGITAL, !val)
                          }
                          if (
                            categoryName === SDF_PAPER_BASED_EXAM &&
                            code === PAPERFORMATS_BRAILLE &&
                            !val
                          ) {
                            toggleIfFound(SDF_PAPER_BASED_EXAM, HUMAN_READER_PAPER, true)
                            toggleIfFound(SDF_PAPER_BASED_EXAM, HUMAN_READER_DIGITAL, true)
                          }
                        },
                      }}
                    />
                  </div>
                )
              })}
            </div>
          </fieldset>
        )
      }
    }

    return null
  }
)
